import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, itIT } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalShipping as LocalShippingIcon,
  Link as LinkIcon,
  Business as BusinessIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Send as SendIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Preview as PreviewIcon,
  Summarize as SummarizeIcon
} from '@mui/icons-material';

import { suppliersApi, gplOrdersApi, deliveriesApi, supplierInvoicesApi, plannersApi } from '../services/api';

// Import modals
import CreateOrderModal from '../components/refills/CreateOrderModal';
import CreateSupplierModal from '../components/refills/CreateSupplierModal';
import RegisterDeliveryModal from '../components/refills/RegisterDeliveryModal';
import ViewOrderModal from '../components/refills/ViewOrderModal';
import ViewDeliveryModal from '../components/refills/ViewDeliveryModal';
import ViewSupplierModal from '../components/refills/ViewSupplierModal';
import ViewInvoiceModal from '../components/refills/ViewInvoiceModal';
import EditInvoiceModal from '../components/refills/EditInvoiceModal';
import UploadInvoiceModal from '../components/refills/UploadInvoiceModal';
import ReconciliationTab from '../components/refills/ReconciliationTab';
import LinkingModal from '../components/refills/LinkingModal';
import EditSupplierModal from '../components/refills/EditSupplierModal';
import PlannerFormModal from '../components/refills/PlannerFormModal';
import ViewPlannerModal from '../components/refills/ViewPlannerModal';
import AssignPlantsModal from '../components/refills/AssignPlantsModal';

// Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`refills-tabpanel-${index}`}
      aria-labelledby={`refills-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const RefillsPage = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Pagination states
  const [ordersPagination, setOrdersPagination] = useState({ page: 0, pageSize: 25 });
  const [deliveriesPagination, setDeliveriesPagination] = useState({ page: 0, pageSize: 25 });
  const [suppliersPagination, setSuppliersPagination] = useState({ page: 0, pageSize: 25 });
  const [invoicesPagination, setInvoicesPagination] = useState({ page: 0, pageSize: 100 });

  // Filtri fatture fornitore
  const [invoicesFilters, setInvoicesFilters] = useState({
    supplier_id: '',
    year: new Date().getFullYear().toString(),
    month: ''
  });

  // Filtro per fattura fornitore
  const [invoiceFilter, setInvoiceFilter] = useState('');

  // Stato per fatture espanse (mostra DDT)
  const [expandedInvoices, setExpandedInvoices] = useState({});

  // Stato per selezione ordini bulk email
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailPreviewMode, setEmailPreviewMode] = useState('summary'); // 'summary' | 'html'
  const [selectedEmailGroup, setSelectedEmailGroup] = useState(0); // Indice del gruppo selezionato per anteprima HTML
  const [emailEdits, setEmailEdits] = useState({}); // Modifiche manuali: { [groupIndex]: { to, cc, subject, html } }
  const [editingField, setEditingField] = useState(null); // Campo attualmente in modifica

  // Modal states
  const [modals, setModals] = useState({
    createOrder: false,
    viewOrder: null,
    createDelivery: false,
    viewDelivery: null,
    createSupplier: false,
    viewSupplier: null,
    editSupplier: null,
    uploadInvoice: false,
    viewInvoice: null,
    editInvoice: null,
    linking: null,
    // Pianificatori
    createPlanner: false,
    viewPlanner: null,
    editPlanner: null,
    assignPlantsToPlanner: null
  });

  // Stato per pianificatori
  const [plannersPagination, setPlannersPagination] = useState({ page: 0, pageSize: 25 });
  const [plannersSearchTerm, setPlannersSearchTerm] = useState('');

  // Queries
  const ordersQuery = useQuery({
    queryKey: ['gplOrders', ordersPagination],
    queryFn: () => gplOrdersApi.getAll({
      page: ordersPagination.page + 1,
      limit: ordersPagination.pageSize
    }),
    keepPreviousData: true
  });

  const deliveriesQuery = useQuery({
    queryKey: ['deliveries', deliveriesPagination, invoiceFilter],
    queryFn: () => deliveriesApi.getAll({
      page: deliveriesPagination.page + 1,
      limit: deliveriesPagination.pageSize,
      supplier_invoice_id: invoiceFilter || undefined
    }),
    keepPreviousData: true
  });

  // Query per lista fatture fornitore (per filtro)
  const allInvoicesQuery = useQuery({
    queryKey: ['allSupplierInvoices'],
    queryFn: () => supplierInvoicesApi.getAll({ limit: 100 }),
    staleTime: 60000
  });

  const unlinkedDeliveriesQuery = useQuery({
    queryKey: ['unlinkedDeliveries'],
    queryFn: () => deliveriesApi.getUnlinked(),
    enabled: tabValue >= 1 && tabValue <= 3
  });

  const unlinkedOrdersQuery = useQuery({
    queryKey: ['unlinkedOrders'],
    queryFn: () => gplOrdersApi.getUnlinked(),
    enabled: tabValue >= 1 && tabValue <= 3
  });

  const suppliersQuery = useQuery({
    queryKey: ['suppliers', suppliersPagination],
    queryFn: () => suppliersApi.getAll({
      page: suppliersPagination.page + 1,
      limit: suppliersPagination.pageSize
    }),
    keepPreviousData: true
  });

  // Query per pianificatori
  const plannersQuery = useQuery({
    queryKey: ['planners', plannersPagination, plannersSearchTerm],
    queryFn: () => plannersApi.getAll({
      page: plannersPagination.page + 1,
      limit: plannersPagination.pageSize,
      search: plannersSearchTerm || undefined
    }),
    keepPreviousData: true,
    enabled: tabValue === 4
  });

  // Calcola date range in base ai filtri anno/mese
  const getInvoiceDateRange = () => {
    const { year, month } = invoicesFilters;
    if (!year && !month) return {};
    
    let from_date, to_date;
    if (year && month) {
      // Anno e mese specifico
      const y = parseInt(year);
      const m = parseInt(month);
      from_date = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      to_date = `${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    } else if (year) {
      // Solo anno
      from_date = `${year}-01-01`;
      to_date = `${year}-12-31`;
    }
    return { from_date, to_date };
  };

  const invoicesQuery = useQuery({
    queryKey: ['supplierInvoices', invoicesPagination, invoicesFilters],
    queryFn: () => {
      const dateRange = getInvoiceDateRange();
      return supplierInvoicesApi.getAll({
        page: invoicesPagination.page + 1,
        limit: invoicesPagination.pageSize,
        supplier_id: invoicesFilters.supplier_id || undefined,
        ...dateRange
      });
    },
    keepPreviousData: true
  });

  // Query per lista tutti i fornitori (per il filtro)
  const allSuppliersQuery = useQuery({
    queryKey: ['allSuppliers'],
    queryFn: () => suppliersApi.getAll({ limit: 100 }),
    staleTime: 300000 // 5 minuti
  });

  // Mutations
  const deleteOrderMutation = useMutation({
    mutationFn: (id) => gplOrdersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['gplOrders']);
      queryClient.invalidateQueries(['plant-consumption-forecast']);
      showSnackbar('Ordine annullato con successo', 'success');
    },
    onError: (err) => showSnackbar(err.response?.data?.error || 'Errore annullamento', 'error')
  });

  const deletePermanentOrderMutation = useMutation({
    mutationFn: (id) => gplOrdersApi.deletePermanent(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['gplOrders']);
      queryClient.invalidateQueries(['plant-consumption-forecast']);
      showSnackbar('Ordine eliminato definitivamente', 'success');
    },
    onError: (err) => showSnackbar(err.response?.data?.error || err.response?.data?.message || 'Errore eliminazione', 'error')
  });

  const linkOrderMutation = useMutation({
    mutationFn: ({ deliveryId, orderId }) => deliveriesApi.linkToOrder(deliveryId, orderId),
    onSuccess: () => {
      // Invalida tutte le query correlate per aggiornare automaticamente le liste
      queryClient.invalidateQueries(['gplOrders']);
      queryClient.invalidateQueries(['deliveries']);
      queryClient.invalidateQueries(['unlinkedDeliveries']);
      queryClient.invalidateQueries(['unlinkedOrders']);
      queryClient.invalidateQueries(['reconciliationStats']);
      showSnackbar('Collegamento creato con successo', 'success');
      closeModal('linking');
    },
    onError: (err) => showSnackbar(err.response?.data?.error || 'Errore collegamento', 'error')
  });

  const deleteDeliveryMutation = useMutation({
    mutationFn: (id) => deliveriesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['deliveries']);
      queryClient.invalidateQueries(['unlinkedDeliveries']);
      queryClient.invalidateQueries(['supplierInvoices']);
      queryClient.invalidateQueries(['allSupplierInvoices']);
      showSnackbar('Rifornimento eliminato con successo', 'success');
    },
    onError: (err) => showSnackbar(err.response?.data?.error || 'Errore eliminazione', 'error')
  });

  const deleteSupplierInvoiceMutation = useMutation({
    mutationFn: (id) => supplierInvoicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['supplierInvoices']);
      queryClient.invalidateQueries(['allSupplierInvoices']);
      queryClient.invalidateQueries(['deliveries']);
      showSnackbar('Fattura fornitore eliminata con successo', 'success');
    },
    onError: (err) => showSnackbar(err.response?.data?.error || 'Errore eliminazione fattura', 'error')
  });

  // Mutation per aggiornare fornitore
  const updateSupplierMutation = useMutation({
    mutationFn: ({ id, data }) => suppliersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['suppliers']);
      queryClient.invalidateQueries(['allSuppliers']);
      showSnackbar('Fornitore aggiornato con successo', 'success');
      closeModal('editSupplier');
    },
    onError: (err) => showSnackbar(err.response?.data?.error || 'Errore aggiornamento fornitore', 'error')
  });

  // Mutations per pianificatori
  const createPlannerMutation = useMutation({
    mutationFn: (data) => plannersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['planners']);
      showSnackbar('Pianificatore creato con successo', 'success');
      closeModal('createPlanner');
    },
    onError: (err) => showSnackbar(err.response?.data?.error || 'Errore creazione pianificatore', 'error')
  });

  const updatePlannerMutation = useMutation({
    mutationFn: ({ id, data }) => plannersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['planners']);
      showSnackbar('Pianificatore aggiornato con successo', 'success');
      closeModal('editPlanner');
    },
    onError: (err) => showSnackbar(err.response?.data?.error || 'Errore aggiornamento pianificatore', 'error')
  });

  const deletePlannerMutation = useMutation({
    mutationFn: (id) => plannersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['planners']);
      showSnackbar('Pianificatore eliminato con successo', 'success');
    },
    onError: (err) => showSnackbar(err.response?.data?.error || 'Errore eliminazione pianificatore', 'error')
  });

  // Funzioni per invio bulk email ordini
  const handlePreviewOrdersEmail = async () => {
    if (selectedOrders.length === 0) {
      showSnackbar('Seleziona almeno un ordine da inviare', 'warning');
      return;
    }
    try {
      setEmailSending(true);
      const response = await gplOrdersApi.sendBulkPreview(selectedOrders);
      if (response.data?.success) {
        setEmailPreviewData(response.data);
        // Inizializza le modifiche per ogni gruppo
        const initialEdits = {};
        response.data.groups.forEach((group, idx) => {
          if (group.emailPreview) {
            initialEdits[idx] = {
              to: group.emailPreview.to || '',
              cc: group.emailPreview.cc || '',
              subject: group.emailPreview.subject || '',
              html: group.emailPreview.html || ''
            };
          }
        });
        setEmailEdits(initialEdits);
        setSelectedEmailGroup(0);
        setEmailPreviewMode('summary');
        setEmailPreviewOpen(true);
      } else {
        showSnackbar(response.data?.message || 'Errore generazione anteprima', 'error');
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Errore nella generazione dell\'anteprima', 'error');
    } finally {
      setEmailSending(false);
    }
  };

  const handleSendOrdersEmail = async () => {
    try {
      setEmailSending(true);
      // Prepara le modifiche da inviare
      const emailOverrides = {};
      Object.entries(emailEdits).forEach(([idx, edits]) => {
        const group = emailPreviewData.groups[parseInt(idx)];
        if (group && group.canSend) {
          const supplierId = group.supplier?.id;
          if (supplierId) {
            emailOverrides[supplierId] = {
              to: edits.to,
              cc: edits.cc,
              subject: edits.subject,
              html: edits.html
            };
          }
        }
      });
      
      const response = await gplOrdersApi.sendBulk(selectedOrders, emailOverrides);
      if (response.data?.success) {
        showSnackbar(`Email inviate con successo a ${response.data.sentCount} fornitore/i`, 'success');
        setEmailPreviewOpen(false);
        setEmailPreviewData(null);
        setEmailEdits({});
        setSelectedOrders([]);
        queryClient.invalidateQueries(['gplOrders']);
      } else {
        showSnackbar(response.data?.message || 'Errore invio email', 'error');
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Errore nell\'invio delle email', 'error');
    } finally {
      setEmailSending(false);
    }
  };

  // Helper per modificare i campi email
  const updateEmailEdit = (groupIndex, field, value) => {
    setEmailEdits(prev => ({
      ...prev,
      [groupIndex]: {
        ...prev[groupIndex],
        [field]: value
      }
    }));
  };

  // Ottieni valore corrente per un campo email (modificato o originale)
  const getEmailFieldValue = (groupIndex, field) => {
    if (emailEdits[groupIndex]?.[field] !== undefined) {
      return emailEdits[groupIndex][field];
    }
    const group = emailPreviewData?.groups?.[groupIndex];
    if (!group?.emailPreview) return '';
    return group.emailPreview[field] || '';
  };

  // Toggle espansione fattura
  const toggleInvoiceExpand = (invoiceId) => {
    setExpandedInvoices(prev => ({
      ...prev,
      [invoiceId]: !prev[invoiceId]
    }));
  };

  // Helpers
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeModal = (modalName) => {
    setModals(prev => ({ ...prev, [modalName]: modalName.startsWith('view') || modalName === 'linking' ? null : false }));
  };

  const openModal = (modalName, data = true) => {
    setModals(prev => ({ ...prev, [modalName]: data }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/D';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  // Column definitions
  const ordersColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'expected_delivery_date', 
      headerName: 'Consegna Prevista', 
      width: 130,
      valueFormatter: (params) => formatDate(params.value) || formatDate(params.row?.order_date)
    },
    { 
      field: 'delivery_code', 
      headerName: 'Cod. Consegna', 
      width: 130,
      valueGetter: (params) => params.row.plant?.delivery_code || 'N/D'
    },
    { 
      field: 'plant', 
      headerName: 'Impianto', 
      width: 200,
      valueGetter: (params) => {
        const plant = params.row.plant;
        const contract = params.row.contract;
        if (plant) {
          return plant.nome || plant.plant_code || `Impianto #${plant.id}`;
        }
        if (contract) {
          return contract.cod_con || contract.nome || `Contratto #${contract.id}`;
        }
        return 'N/D';
      }
    },
    { 
      field: 'plant_address', 
      headerName: 'Indirizzo', 
      width: 200,
      valueGetter: (params) => {
        const plant = params.row.plant;
        if (plant) {
          return [plant.indirizzo, plant.citta, plant.provincia].filter(Boolean).join(', ') || 'N/D';
        }
        return 'N/D';
      }
    },
    { 
      field: 'supplier', 
      headerName: 'Fornitore', 
      width: 180,
      valueGetter: (params) => params.row.supplier?.company_name || 'N/D'
    },
    { 
      field: 'quantity_liters', 
      headerName: 'Quantità (L)', 
      width: 120,
      type: 'number',
      valueFormatter: (params) => parseFloat(params.value || 0).toLocaleString('it-IT')
    },
    { 
      field: 'estimated_amount', 
      headerName: 'Totale', 
      width: 120,
      type: 'number',
      valueFormatter: (params) => formatCurrency(params.value)
    },
    { 
      field: 'status', 
      headerName: 'Stato', 
      width: 120,
      renderCell: (params) => {
        const colors = { pending: 'warning', confirmed: 'info', delivered: 'success', cancelled: 'error', sent: 'primary' };
        const labels = { pending: 'In Attesa', confirmed: 'Confermato', delivered: 'Consegnato', cancelled: 'Annullato', sent: 'Inviato' };
        return <Chip label={labels[params.value] || params.value} color={colors[params.value] || 'default'} size="small" />;
      }
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => openModal('viewOrder', params.row)} title="Visualizza">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          {params.row.status !== 'cancelled' && params.row.status !== 'delivered' && (
            <IconButton 
              size="small" 
              color="warning" 
              onClick={() => {
                if (window.confirm('Sei sicuro di voler annullare questo ordine?')) {
                  deleteOrderMutation.mutate(params.row.id);
                }
              }}
              title="Annulla ordine"
            >
              <CancelIcon fontSize="small" />
            </IconButton>
          )}
          {!params.row.delivery_id && (
            <IconButton 
              size="small" 
              color="error" 
              onClick={() => {
                if (window.confirm('Sei sicuro di voler ELIMINARE DEFINITIVAMENTE questo ordine? Questa azione non può essere annullata.')) {
                  deletePermanentOrderMutation.mutate(params.row.id);
                }
              }}
              title="Elimina definitivamente"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )
    }
  ];

  const deliveriesColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'delivery_date', 
      headerName: 'Data', 
      width: 110,
      valueFormatter: (params) => formatDate(params.value)
    },
    { field: 'ddt_number', headerName: 'DDT', width: 130 },
    { 
      field: 'supplierInvoice', 
      headerName: 'Fattura Fornitore', 
      width: 160,
      renderCell: (params) => {
        const invoice = params.row.supplierInvoice;
        if (!invoice) return <Chip label="N/D" size="small" variant="outlined" />;
        return (
          <Chip 
            label={`${invoice.invoice_number}`} 
            size="small" 
            color="primary" 
            variant="outlined"
            onClick={() => openModal('viewInvoice', invoice)}
            sx={{ cursor: 'pointer' }}
          />
        );
      }
    },
    { 
      field: 'plant_code', 
      headerName: 'Cod. Impianto', 
      width: 120,
      valueGetter: (params) => params.row.plant_code || 'N/D'
    },
    { 
      field: 'contract', 
      headerName: 'Contratto', 
      width: 130,
      valueGetter: (params) => params.row.contract?.cod_con || params.row.contract?.nome || 'N/D'
    },
    { 
      field: 'supplier', 
      headerName: 'Fornitore', 
      width: 150,
      valueGetter: (params) => params.row.supplier?.company_name || 'N/D'
    },
    { 
      field: 'quantity_liters', 
      headerName: 'Quantità (L)', 
      width: 110,
      type: 'number',
      valueFormatter: (params) => parseFloat(params.value || 0).toLocaleString('it-IT')
    },
    { 
      field: 'total_amount', 
      headerName: 'Totale', 
      width: 100,
      type: 'number',
      valueFormatter: (params) => formatCurrency(params.value)
    },
    { 
      field: 'order_id', 
      headerName: 'Ordine', 
      width: 100,
      renderCell: (params) => params.value ? (
        <Chip label={`#${params.value}`} size="small" color="success" icon={<LinkIcon />} />
      ) : (
        <Chip label="Non collegato" size="small" variant="outlined" />
      )
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => openModal('viewDelivery', params.row)} title="Visualizza">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            color="error" 
            onClick={() => {
              if (window.confirm('Sei sicuro di voler eliminare questo rifornimento?')) {
                deleteDeliveryMutation.mutate(params.row.id);
              }
            }}
            title="Elimina"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  const suppliersColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'supplier_code', headerName: 'Codice', width: 100 },
    { field: 'company_name', headerName: 'Ragione Sociale', width: 200 },
    { field: 'vat_number', headerName: 'P.IVA', width: 130 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'phone', headerName: 'Telefono', width: 120 },
    { 
      field: 'current_price_per_liter', 
      headerName: 'Prezzo/L', 
      width: 100,
      type: 'number',
      valueFormatter: (params) => formatCurrency(params.value)
    },
    { 
      field: 'status', 
      headerName: 'Stato', 
      width: 100,
      renderCell: (params) => {
        const colors = { active: 'success', inactive: 'error', suspended: 'warning' };
        const labels = { active: 'Attivo', inactive: 'Inattivo', suspended: 'Sospeso' };
        return <Chip label={labels[params.value] || params.value} color={colors[params.value] || 'default'} size="small" />;
      }
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => openModal('viewSupplier', params.row)} title="Visualizza">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => openModal('editSupplier', params.row)} title="Modifica" color="primary">
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  // Colonne DataGrid per pianificatori
  const plannersColumns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Nome', width: 180 },
    { 
      field: 'supplier', 
      headerName: 'Fornitore', 
      width: 180,
      valueGetter: (params) => params.row.supplier?.company_name || 'N/D'
    },
    { field: 'role', headerName: 'Ruolo', width: 130 },
    { field: 'email', headerName: 'Email', width: 180 },
    { field: 'phone', headerName: 'Telefono', width: 120 },
    { 
      field: 'coverage_provinces', 
      headerName: 'Province', 
      width: 180,
      renderCell: (params) => {
        let provinces = params.value || [];
        if (typeof provinces === 'string') {
          try { provinces = JSON.parse(provinces); } catch (e) { provinces = []; }
        }
        if (!Array.isArray(provinces) || provinces.length === 0) return '-';
        const display = provinces.slice(0, 3).join(', ');
        return provinces.length > 3 ? `${display} (+${provinces.length - 3})` : display;
      }
    },
    { 
      field: 'is_primary', 
      headerName: 'Primario', 
      width: 90,
      renderCell: (params) => params.value ? <Chip label="Sì" color="warning" size="small" /> : '-'
    },
    { 
      field: 'is_active', 
      headerName: 'Attivo', 
      width: 90,
      renderCell: (params) => (
        <Chip 
          label={params.value !== false ? 'Sì' : 'No'} 
          color={params.value !== false ? 'success' : 'default'} 
          size="small" 
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => openModal('viewPlanner', params.row)} title="Visualizza">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => openModal('editPlanner', params.row)} title="Modifica" color="primary">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={() => {
              if (window.confirm(`Eliminare il pianificatore ${params.row.name}?`)) {
                deletePlannerMutation.mutate(params.row.id);
              }
            }} 
            title="Elimina" 
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Typography variant={isMobile ? 'h6' : 'h4'} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalShippingIcon fontSize={isMobile ? 'medium' : 'large'} color="primary" />
        Rifornimenti GPL
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab icon={<ShoppingCartIcon />} label={isMobile ? 'Ordini' : 'Ordini'} iconPosition="start" />
          <Tab icon={<LocalShippingIcon />} label={isMobile ? 'Rifornim.' : 'Rifornimenti'} iconPosition="start" />
          <Tab icon={<ReceiptIcon />} label={isMobile ? 'Fatture' : 'Fatture Fornitore'} iconPosition="start" />
          <Tab icon={<LinkIcon />} label={isMobile ? 'Riconc.' : 'Riconciliazione'} iconPosition="start" />
          <Tab icon={<BusinessIcon />} label="Fornitori" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0: Ordini */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            <TextField
              placeholder="Cerca ordini..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ width: { xs: '100%', sm: 250 } }}
            />
            {selectedOrders.length > 0 && (
              <Chip 
                label={`${selectedOrders.length} ordini selezionati`} 
                color="primary" 
                variant="outlined"
                onDelete={() => setSelectedOrders([])}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color="success"
              size={isMobile ? 'small' : 'medium'}
              startIcon={emailSending ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={handlePreviewOrdersEmail}
              disabled={selectedOrders.length === 0 || emailSending}
            >
              {isMobile ? `Invia (${selectedOrders.length})` : `Invia Ordini Selezionati (${selectedOrders.length})`}
            </Button>
            {!isMobile && <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => queryClient.invalidateQueries(['gplOrders'])}
            >
              Aggiorna
            </Button>}
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<AddIcon />}
              onClick={() => openModal('createOrder')}
            >
              {isMobile ? 'Nuovo' : 'Nuovo Ordine'}
            </Button>
          </Box>
        </Box>

        <Paper sx={{ height: { xs: 400, sm: 600 }, overflow: 'auto' }}>
          <DataGrid
            rows={ordersQuery.data?.data?.data || []}
            columns={ordersColumns}
            rowCount={ordersQuery.data?.data?.pagination?.total || 0}
            loading={ordersQuery.isLoading}
            paginationMode="server"
            paginationModel={ordersPagination}
            onPaginationModelChange={setOrdersPagination}
            pageSizeOptions={[10, 25, 50]}
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
            checkboxSelection
            rowSelectionModel={selectedOrders}
            onRowSelectionModelChange={(newSelection) => setSelectedOrders(newSelection)}
            isRowSelectable={(params) => params.row.status === 'pending' && !params.row.email_sent}
          />
        </Paper>
      </TabPanel>

      {/* Tab 1: Rifornimenti Fatturati */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
            <TextField
              placeholder="Cerca rifornimenti..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ width: { xs: '100%', sm: 250 } }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 250 } }}>
              <InputLabel>Filtra per Fattura</InputLabel>
              <Select
                value={invoiceFilter}
                label="Filtra per Fattura"
                onChange={(e) => {
                  setInvoiceFilter(e.target.value);
                  setDeliveriesPagination({ ...deliveriesPagination, page: 0 });
                }}
              >
                <MenuItem value="">
                  <em>Tutte le fatture</em>
                </MenuItem>
                {allInvoicesQuery.data?.data?.data?.map((inv) => (
                  <MenuItem key={inv.id} value={inv.id}>
                    {inv.invoice_number} - {formatDate(inv.invoice_date)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {invoiceFilter && (
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => setInvoiceFilter('')}
              >
                Rimuovi Filtro
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {!isMobile && <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => queryClient.invalidateQueries(['deliveries'])}
            >
              Aggiorna
            </Button>}
            <Button
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<CloudUploadIcon />}
              onClick={() => openModal('uploadInvoice')}
            >
              {isMobile ? 'Importa XML' : 'Importa Fattura XML'}
            </Button>
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<AddIcon />}
              onClick={() => openModal('createDelivery')}
            >
              {isMobile ? 'Nuovo' : 'Registra Rifornimento'}
            </Button>
          </Box>
        </Box>

        <Paper sx={{ height: { xs: 400, sm: 600 }, overflow: 'auto' }}>
          <DataGrid
            rows={deliveriesQuery.data?.data?.data || []}
            columns={deliveriesColumns}
            rowCount={deliveriesQuery.data?.data?.pagination?.total || 0}
            loading={deliveriesQuery.isLoading}
            paginationMode="server"
            paginationModel={deliveriesPagination}
            onPaginationModelChange={setDeliveriesPagination}
            pageSizeOptions={[10, 25, 50]}
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
            disableRowSelectionOnClick
          />
        </Paper>
      </TabPanel>

      {/* Tab 2: Fatture Fornitore */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
            Fatture Fornitore ({invoicesQuery.data?.data?.pagination?.total || 0})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isMobile && <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => queryClient.invalidateQueries(['supplierInvoices'])}
            >
              Aggiorna
            </Button>}
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<CloudUploadIcon />}
              onClick={() => openModal('uploadInvoice')}
            >
              {isMobile ? 'Importa XML' : 'Importa Fattura XML'}
            </Button>
          </Box>
        </Box>

        {/* Filtri fatture fornitore */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>Fornitore</InputLabel>
              <Select
                value={invoicesFilters.supplier_id}
                label="Fornitore"
                onChange={(e) => {
                  setInvoicesFilters(prev => ({ ...prev, supplier_id: e.target.value }));
                  setInvoicesPagination(prev => ({ ...prev, page: 0 }));
                }}
              >
                <MenuItem value="">Tutti i fornitori</MenuItem>
                {(allSuppliersQuery.data?.data?.data || []).map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.company_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Anno</InputLabel>
              <Select
                value={invoicesFilters.year}
                label="Anno"
                onChange={(e) => {
                  setInvoicesFilters(prev => ({ ...prev, year: e.target.value }));
                  setInvoicesPagination(prev => ({ ...prev, page: 0 }));
                }}
              >
                <MenuItem value="">Tutti</MenuItem>
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(y => (
                  <MenuItem key={y} value={y.toString()}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Mese</InputLabel>
              <Select
                value={invoicesFilters.month}
                label="Mese"
                onChange={(e) => {
                  setInvoicesFilters(prev => ({ ...prev, month: e.target.value }));
                  setInvoicesPagination(prev => ({ ...prev, page: 0 }));
                }}
              >
                <MenuItem value="">Tutti i mesi</MenuItem>
                {[
                  { value: '1', label: 'Gennaio' },
                  { value: '2', label: 'Febbraio' },
                  { value: '3', label: 'Marzo' },
                  { value: '4', label: 'Aprile' },
                  { value: '5', label: 'Maggio' },
                  { value: '6', label: 'Giugno' },
                  { value: '7', label: 'Luglio' },
                  { value: '8', label: 'Agosto' },
                  { value: '9', label: 'Settembre' },
                  { value: '10', label: 'Ottobre' },
                  { value: '11', label: 'Novembre' },
                  { value: '12', label: 'Dicembre' }
                ].map(m => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {(invoicesFilters.supplier_id || invoicesFilters.year || invoicesFilters.month) && (
              <Button
                variant="text"
                size="small"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setInvoicesFilters({ supplier_id: '', year: '', month: '' });
                  setInvoicesPagination(prev => ({ ...prev, page: 0 }));
                }}
              >
                Rimuovi filtri
              </Button>
            )}
          </Box>
        </Paper>

        {/* Riepilogo Totali Fatture Filtrate */}
        {!invoicesQuery.isLoading && invoicesQuery.data?.data?.data?.length > 0 && (
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
            <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  N. FATTURE
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  {invoicesQuery.data?.data?.data?.length || 0}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  TOTALE LITRI
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="info.main">
                  {(invoicesQuery.data?.data?.data || [])
                    .reduce((sum, inv) => sum + parseFloat(inv.quantity_liters || 0), 0)
                    .toLocaleString('it-IT', { maximumFractionDigits: 0 })} L
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  TOTALE IMPONIBILE
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="text.primary">
                  € {(invoicesQuery.data?.data?.data || [])
                    .reduce((sum, inv) => sum + parseFloat(inv.taxable_amount || 0), 0)
                    .toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  TOTALE FATTURE (IVA incl.)
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  € {(invoicesQuery.data?.data?.data || [])
                    .reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0)
                    .toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  PREZZO MEDIO €/L
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="warning.main">
                  {(() => {
                    const totLitri = (invoicesQuery.data?.data?.data || [])
                      .reduce((sum, inv) => sum + parseFloat(inv.quantity_liters || 0), 0);
                    const totImponibile = (invoicesQuery.data?.data?.data || [])
                      .reduce((sum, inv) => sum + parseFloat(inv.taxable_amount || 0), 0);
                    if (totLitri > 0 && totImponibile > 0) {
                      return `€ ${(totImponibile / totLitri).toFixed(4)}`;
                    }
                    return '-';
                  })()}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        <Alert severity="info" sx={{ mb: 2 }}>
          Clicca su una fattura per espanderla e vedere i DDT (rifornimenti) associati. 
          Puoi eliminare la fattura insieme a tutti i rifornimenti collegati.
        </Alert>

        {invoicesQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {(invoicesQuery.data?.data?.data || []).map((invoice) => (
              <Card key={invoice.id} variant="outlined">
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  {/* Header fattura */}
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: isMobile ? 'column' : 'row',
                      justifyContent: 'space-between', 
                      alignItems: isMobile ? 'stretch' : 'center',
                      gap: isMobile ? 1 : 0
                    }}
                  >
                    <Box 
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flex: 1, minWidth: 0 }}
                      onClick={() => toggleInvoiceExpand(invoice.id)}
                    >
                      <IconButton size="small">
                        {expandedInvoices[invoice.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap>
                          Fattura N. {invoice.invoice_number}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          Data: {formatDate(invoice.invoice_date)} | 
                          Fornitore: {invoice.supplier?.company_name || 'N/D'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 2, flexWrap: 'wrap', justifyContent: isMobile ? 'space-between' : 'flex-end', pl: isMobile ? 4 : 0 }}>
                      <Box sx={{ textAlign: 'right', minWidth: isMobile ? 'auto' : 80 }}>
                        <Typography variant="caption" color="text.secondary">Litri</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {parseFloat(invoice.quantity_liters || 0).toLocaleString('it-IT')} L
                        </Typography>
                      </Box>
                      {!isMobile && (
                        <Box sx={{ textAlign: 'right', minWidth: 70 }}>
                          <Typography variant="caption" color="text.secondary">€/L</Typography>
                          <Typography variant="body2" fontWeight="bold" color="info.main">
                            {(() => {
                              const liters = parseFloat(invoice.quantity_liters) || 0;
                              const taxable = parseFloat(invoice.taxable_amount) || 0;
                              if (liters > 0 && taxable > 0) {
                                return `€ ${(taxable / liters).toFixed(4)}`;
                              }
                              return '-';
                            })()}
                          </Typography>
                        </Box>
                      )}
                      {!isMobile && (
                        <Box sx={{ textAlign: 'right', minWidth: 90 }}>
                          <Typography variant="caption" color="text.secondary">Imponibile</Typography>
                          <Typography variant="body2">
                            {formatCurrency(invoice.taxable_amount)}
                          </Typography>
                        </Box>
                      )}
                      {!isMobile && (
                        <Box sx={{ textAlign: 'right', minWidth: 70 }}>
                          <Typography variant="caption" color="text.secondary">IVA</Typography>
                          <Typography variant="body2">
                            {formatCurrency(invoice.vat_amount)}
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ textAlign: 'right', minWidth: isMobile ? 'auto' : 90 }}>
                        <Typography variant="caption" color="text.secondary">Totale</Typography>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          {formatCurrency(invoice.total_amount)}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${invoice.deliveries?.length || 0} DDT`} 
                        size="small" 
                        color="info"
                      />
                      <Box sx={{ display: 'flex', gap: 0 }}>
                        <IconButton 
                          size="small" 
                          onClick={() => openModal('viewInvoice', invoice)}
                          title="Visualizza dettagli"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="primary"
                          onClick={() => openModal('editInvoice', invoice)}
                          title="Modifica fattura e DDT"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => {
                            const ddtCount = invoice.deliveries?.length || 0;
                            if (window.confirm(`Sei sicuro di voler eliminare la fattura ${invoice.invoice_number}?\n\nVerranno eliminati anche ${ddtCount} rifornimenti (DDT) associati.`)) {
                              deleteSupplierInvoiceMutation.mutate(invoice.id);
                            }
                          }}
                          title="Elimina fattura e DDT"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>

                  {/* Dettaglio DDT espandibile */}
                  <Collapse in={expandedInvoices[invoice.id]}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      DDT / Rifornimenti associati:
                    </Typography>
                    {invoice.deliveries && invoice.deliveries.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>DDT</TableCell>
                              <TableCell>Data</TableCell>
                              <TableCell>Cod. Impianto</TableCell>
                              <TableCell>Cod. Consegna</TableCell>
                              <TableCell>Indirizzo</TableCell>
                              <TableCell align="right">Quantità (L)</TableCell>
                              <TableCell align="right">€/L</TableCell>
                              <TableCell align="right">Imponibile</TableCell>
                              <TableCell align="right">IVA</TableCell>
                              <TableCell align="right">Totale</TableCell>
                              <TableCell align="center">Azioni</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {invoice.deliveries.map((delivery) => (
                              <TableRow key={delivery.id} hover>
                                <TableCell>{delivery.ddt_number || 'N/D'}</TableCell>
                                <TableCell>{formatDate(delivery.delivery_date)}</TableCell>
                                <TableCell>
                                  {delivery.plant?.plant_code || '-'}
                                </TableCell>
                                <TableCell>
                                  {delivery.plant?.delivery_code || delivery.plant_code || 'N/D'}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {delivery.plant?.indirizzo || delivery.plant_address || 'N/D'}
                                  {delivery.plant?.citta ? `, ${delivery.plant.citta}` : ''}
                                </TableCell>
                                <TableCell align="right">
                                  {parseFloat(delivery.quantity_liters || 0).toLocaleString('it-IT')}
                                </TableCell>
                                <TableCell align="right">
                                  {(() => {
                                    // Calcola prezzo al litro: imponibile / litri
                                    const liters = parseFloat(delivery.quantity_liters) || 0;
                                    const taxable = parseFloat(delivery.taxable_amount) || 0;
                                    if (liters > 0 && taxable > 0) {
                                      return `€ ${(taxable / liters).toFixed(4)}`;
                                    }
                                    return '-';
                                  })()}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(delivery.taxable_amount)}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(delivery.vat_amount)}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                  {formatCurrency(delivery.total_amount)}
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => openModal('viewDelivery', delivery)}
                                    title="Visualizza"
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton 
                                    size="small" 
                                    color="error"
                                    onClick={() => {
                                      if (window.confirm('Eliminare questo rifornimento?')) {
                                        deleteDeliveryMutation.mutate(delivery.id);
                                      }
                                    }}
                                    title="Elimina DDT"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Nessun DDT associato a questa fattura
                      </Typography>
                    )}
                  </Collapse>
                </CardContent>
              </Card>
            ))}

            {(invoicesQuery.data?.data?.data || []).length === 0 && (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  Nessuna fattura fornitore importata
                </Typography>
              </Paper>
            )}
          </Box>
        )}
      </TabPanel>

      {/* Tab 3: Riconciliazione */}
      <TabPanel value={tabValue} index={3}>
        <ReconciliationTab 
          onOpenLinkingModal={(data) => openModal('linking', data)}
        />
      </TabPanel>

      {/* Tab 4: Fornitori e Pianificatori */}
      <TabPanel value={tabValue} index={4}>
        {/* Sezione Fornitori */}
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon color="primary" />
          Fornitori
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <TextField
            placeholder="Cerca fornitori..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />
          <Button
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<AddIcon />}
            onClick={() => openModal('createSupplier')}
          >
            {isMobile ? 'Nuovo' : 'Nuovo Fornitore'}
          </Button>
        </Box>

        <Paper sx={{ height: { xs: 300, sm: 400 }, mb: 4, overflow: 'auto' }}>
          <DataGrid
            rows={suppliersQuery.data?.data?.data || []}
            columns={suppliersColumns}
            rowCount={suppliersQuery.data?.data?.pagination?.total || 0}
            loading={suppliersQuery.isLoading}
            paginationMode="server"
            paginationModel={suppliersPagination}
            onPaginationModelChange={setSuppliersPagination}
            pageSizeOptions={[10, 25, 50]}
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
            disableRowSelectionOnClick
          />
        </Paper>

        {/* Sezione Pianificatori */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          Pianificatori
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          I pianificatori sono i referenti dei fornitori che gestiscono i rifornimenti e coprono determinati impianti.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <TextField
            placeholder="Cerca pianificatori..."
            size="small"
            value={plannersSearchTerm}
            onChange={(e) => setPlannersSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />
          <Button
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            startIcon={<AddIcon />}
            onClick={() => openModal('createPlanner')}
          >
            {isMobile ? 'Nuovo' : 'Nuovo Pianificatore'}
          </Button>
        </Box>

        <Paper sx={{ height: { xs: 300, sm: 400 }, overflow: 'auto' }}>
          <DataGrid
            rows={plannersQuery.data?.data?.data || []}
            columns={plannersColumns}
            rowCount={plannersQuery.data?.data?.pagination?.total || 0}
            loading={plannersQuery.isLoading}
            paginationMode="server"
            paginationModel={plannersPagination}
            onPaginationModelChange={setPlannersPagination}
            pageSizeOptions={[10, 25, 50]}
            localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
            disableRowSelectionOnClick
          />
        </Paper>
      </TabPanel>

      {/* Modals */}
      <CreateOrderModal
        open={modals.createOrder}
        onClose={() => closeModal('createOrder')}
        onSuccess={() => {
          queryClient.invalidateQueries(['gplOrders']);
          queryClient.invalidateQueries(['plant-consumption-forecast']);
          queryClient.invalidateQueries(['plant-level-info']);
          closeModal('createOrder');
          showSnackbar('Ordine creato con successo');
        }}
      />

      <ViewOrderModal
        open={!!modals.viewOrder}
        onClose={() => closeModal('viewOrder')}
        order={modals.viewOrder}
        onLinkDelivery={(order) => openModal('linking', { source: order, type: 'order-to-delivery' })}
      />

      <RegisterDeliveryModal
        open={modals.createDelivery}
        onClose={() => closeModal('createDelivery')}
        onSuccess={() => {
          queryClient.invalidateQueries(['deliveries']);
          closeModal('createDelivery');
          showSnackbar('Rifornimento registrato con successo');
        }}
      />

      <ViewDeliveryModal
        open={!!modals.viewDelivery}
        onClose={() => closeModal('viewDelivery')}
        delivery={modals.viewDelivery}
        onLinkOrder={(delivery) => openModal('linking', { source: delivery, type: 'delivery-to-order' })}
      />

      <CreateSupplierModal
        open={modals.createSupplier}
        onClose={() => closeModal('createSupplier')}
        onSuccess={() => {
          queryClient.invalidateQueries(['suppliers']);
          closeModal('createSupplier');
          showSnackbar('Fornitore creato con successo');
        }}
      />

      <ViewSupplierModal
        open={!!modals.viewSupplier}
        onClose={() => closeModal('viewSupplier')}
        supplier={modals.viewSupplier}
        onEdit={(supplier) => {
          closeModal('viewSupplier');
          openModal('editSupplier', supplier);
        }}
      />

      <EditSupplierModal
        open={!!modals.editSupplier}
        onClose={() => closeModal('editSupplier')}
        supplier={modals.editSupplier}
        onSubmit={(data) => {
          updateSupplierMutation.mutate({ id: modals.editSupplier.id, data });
        }}
        loading={updateSupplierMutation.isLoading}
      />

      {/* Modali Pianificatori */}
      <PlannerFormModal
        open={modals.createPlanner}
        onClose={() => closeModal('createPlanner')}
        planner={null}
        suppliers={allSuppliersQuery.data?.data?.data || []}
        onSubmit={(data) => createPlannerMutation.mutate(data)}
        loading={createPlannerMutation.isLoading}
      />

      <PlannerFormModal
        open={!!modals.editPlanner}
        onClose={() => closeModal('editPlanner')}
        planner={modals.editPlanner}
        suppliers={allSuppliersQuery.data?.data?.data || []}
        onSubmit={(data) => updatePlannerMutation.mutate({ id: modals.editPlanner.id, data })}
        loading={updatePlannerMutation.isLoading}
      />

      <ViewPlannerModal
        open={!!modals.viewPlanner}
        onClose={() => closeModal('viewPlanner')}
        planner={modals.viewPlanner}
        supplierName={
          modals.viewPlanner?.supplier?.company_name ||
          allSuppliersQuery.data?.data?.data?.find(s => s.id === modals.viewPlanner?.supplier_id)?.company_name
        }
        onEdit={(planner) => {
          closeModal('viewPlanner');
          openModal('editPlanner', planner);
        }}
        onAssignPlants={(planner) => {
          closeModal('viewPlanner');
          openModal('assignPlantsToPlanner', planner);
        }}
      />

      <AssignPlantsModal
        open={!!modals.assignPlantsToPlanner}
        onClose={() => closeModal('assignPlantsToPlanner')}
        planner={modals.assignPlantsToPlanner}
        onSuccess={() => {
          queryClient.invalidateQueries(['planners']);
          showSnackbar('Impianti assegnati con successo');
        }}
      />

      <UploadInvoiceModal
        open={modals.uploadInvoice}
        onClose={() => closeModal('uploadInvoice')}
        onSuccess={(imported) => {
          queryClient.invalidateQueries(['supplierInvoices']);
          queryClient.invalidateQueries(['deliveries']);
          const count = Array.isArray(imported) ? imported.length : 1;
          showSnackbar(`${count} fattur${count === 1 ? 'a importata' : 'e importate'} con successo`);
        }}
      />

      <ViewInvoiceModal
        open={!!modals.viewInvoice}
        onClose={() => closeModal('viewInvoice')}
        invoice={modals.viewInvoice}
        onEdit={(invoice) => {
          closeModal('viewInvoice');
          openModal('editInvoice', invoice);
        }}
      />

      <EditInvoiceModal
        open={!!modals.editInvoice}
        onClose={() => closeModal('editInvoice')}
        invoice={modals.editInvoice}
        onSuccess={() => {
          queryClient.invalidateQueries(['supplierInvoices']);
          queryClient.invalidateQueries(['deliveries']);
          showSnackbar('Fattura aggiornata con successo');
        }}
      />

      {modals.linking && (
        <LinkingModal
          open={!!modals.linking}
          onClose={() => closeModal('linking')}
          source={modals.linking.source}
          targets={modals.linking.type === 'order-to-delivery' 
            ? (unlinkedDeliveriesQuery.data?.data?.data || [])
            : (unlinkedOrdersQuery.data?.data?.data || [])
          }
          type={modals.linking.type}
          onLink={(orderId, deliveryId) => linkOrderMutation.mutate({ deliveryId, orderId })}
          loading={linkOrderMutation.isLoading}
        />
      )}

      {/* Dialog Anteprima Email Ordini */}
      <Dialog 
        open={emailPreviewOpen} 
        onClose={() => !emailSending && setEmailPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            Anteprima Invio Ordini Email
          </Box>
          {emailPreviewData?.groups?.length > 0 && (
            <ToggleButtonGroup
              value={emailPreviewMode}
              exclusive
              onChange={(e, val) => val && setEmailPreviewMode(val)}
              size="small"
            >
              <ToggleButton value="summary">
                <SummarizeIcon sx={{ mr: 0.5 }} fontSize="small" />
                Riepilogo
              </ToggleButton>
              <ToggleButton value="html">
                <PreviewIcon sx={{ mr: 0.5 }} fontSize="small" />
                Anteprima Email
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {emailPreviewData?.groups?.length > 0 ? (
            <>
              {emailPreviewMode === 'summary' ? (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Verranno inviate <strong>{emailPreviewData.totalGroups}</strong> email per un totale di <strong>{emailPreviewData.totalOrders}</strong> ordini.
                  </Alert>
                  
                  {emailPreviewData.groups.map((group, idx) => (
                    <Card key={idx} sx={{ mb: 2, border: !group.canSend ? '2px solid #f44336' : '1px solid #e0e0e0' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BusinessIcon fontSize="small" />
                              {group.supplier?.company_name || 'Fornitore sconosciuto'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <EmailIcon fontSize="small" />
                              TO: {group.supplierEmail}
                            </Typography>
                            {group.plannerEmail && (
                              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PersonIcon fontSize="small" />
                                CC: {group.plannerEmail} ({group.planner?.name || 'Pianificatore'})
                              </Typography>
                            )}
                          </Box>
                          {!group.canSend && (
                            <Chip label="Email mancante" color="error" size="small" />
                          )}
                        </Box>
                        
                        <Divider sx={{ my: 1 }} />
                        
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>{group.orderCount}</strong> impianti • <strong>{new Intl.NumberFormat('it-IT').format(group.totalLiters)}</strong> litri totali
                          {group.urgentCount > 0 && (
                            <Chip label={`${group.urgentCount} urgenti`} size="small" color="warning" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                        
                        <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1 }}>
                          {group.orders.map((order, oIdx) => (
                            <ListItem key={oIdx} sx={{ py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <LocalShippingIcon fontSize="small" color="action" />
                              </ListItemIcon>
                              <ListItemText
                                primary={order.plant?.nome || order.plant?.codice || `Impianto #${order.plant_id}`}
                                secondary={`${new Intl.NumberFormat('it-IT').format(order.quantity_liters || order.quantita_ordinata || 0)} L${order.priority && order.priority !== 'normal' ? ` • ${order.priority.toUpperCase()}` : ''}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  ))}
                </>
              ) : (
                /* Modalità Anteprima HTML con Editing */
                <Box>
                  {/* Selettore email se ci sono più gruppi */}
                  {emailPreviewData.groups.length > 1 && (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Seleziona email da visualizzare/modificare</InputLabel>
                      <Select
                        value={selectedEmailGroup}
                        label="Seleziona email da visualizzare/modificare"
                        onChange={(e) => setSelectedEmailGroup(e.target.value)}
                      >
                        {emailPreviewData.groups.map((group, idx) => (
                          <MenuItem key={idx} value={idx}>
                            {group.supplier?.company_name || 'Fornitore sconosciuto'} ({group.orderCount} ordini)
                            {emailEdits[idx] && Object.keys(emailEdits[idx]).some(k => 
                              emailEdits[idx][k] !== group.emailPreview?.[k]
                            ) && ' ✏️'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  
                  {emailPreviewData.groups[selectedEmailGroup]?.emailPreview || emailEdits[selectedEmailGroup] ? (
                    <Box>
                      {/* Header email editabile */}
                      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ width: 70, fontWeight: 'bold' }}>A:</Typography>
                          <TextField
                            size="small"
                            fullWidth
                            value={getEmailFieldValue(selectedEmailGroup, 'to')}
                            onChange={(e) => updateEmailEdit(selectedEmailGroup, 'to', e.target.value)}
                            placeholder="email@esempio.com, altro@esempio.com"
                            helperText="Separare più indirizzi con virgola"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ width: 70, fontWeight: 'bold' }}>CC:</Typography>
                          <TextField
                            size="small"
                            fullWidth
                            value={getEmailFieldValue(selectedEmailGroup, 'cc')}
                            onChange={(e) => updateEmailEdit(selectedEmailGroup, 'cc', e.target.value)}
                            placeholder="copia@esempio.com (opzionale)"
                            helperText="Separare più indirizzi con virgola"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ width: 70, fontWeight: 'bold' }}>Oggetto:</Typography>
                          <TextField
                            size="small"
                            fullWidth
                            value={getEmailFieldValue(selectedEmailGroup, 'subject')}
                            onChange={(e) => updateEmailEdit(selectedEmailGroup, 'subject', e.target.value)}
                          />
                        </Box>
                      </Paper>
                      
                      {/* Toggle tra anteprima e modifica HTML */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">Corpo Email</Typography>
                        <ToggleButtonGroup
                          value={editingField === 'html' ? 'edit' : 'preview'}
                          exclusive
                          onChange={(e, val) => setEditingField(val === 'edit' ? 'html' : null)}
                          size="small"
                        >
                          <ToggleButton value="preview">
                            <PreviewIcon sx={{ mr: 0.5 }} fontSize="small" />
                            Anteprima
                          </ToggleButton>
                          <ToggleButton value="edit">
                            <EditIcon sx={{ mr: 0.5 }} fontSize="small" />
                            Modifica HTML
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Box>
                      
                      {/* Corpo email - Anteprima o Editor */}
                      {editingField === 'html' ? (
                        <TextField
                          multiline
                          fullWidth
                          minRows={15}
                          maxRows={25}
                          value={getEmailFieldValue(selectedEmailGroup, 'html')}
                          onChange={(e) => updateEmailEdit(selectedEmailGroup, 'html', e.target.value)}
                          sx={{ 
                            fontFamily: 'monospace',
                            '& .MuiInputBase-input': { fontSize: '12px' }
                          }}
                        />
                      ) : (
                        <Paper 
                          sx={{ 
                            p: 0, 
                            border: '1px solid #ddd',
                            maxHeight: '50vh',
                            overflow: 'auto'
                          }}
                        >
                          <iframe
                            title="Email Preview"
                            srcDoc={getEmailFieldValue(selectedEmailGroup, 'html')}
                            style={{
                              width: '100%',
                              minHeight: '400px',
                              border: 'none'
                            }}
                          />
                        </Paper>
                      )}
                      
                      {/* Indicatore modifiche */}
                      {emailEdits[selectedEmailGroup] && (
                        <Alert severity="info" sx={{ mt: 2 }} icon={<EditIcon />}>
                          Hai modificato questa email. Le modifiche verranno applicate all'invio.
                          <Button 
                            size="small" 
                            sx={{ ml: 2 }}
                            onClick={() => {
                              const group = emailPreviewData.groups[selectedEmailGroup];
                              if (group?.emailPreview) {
                                setEmailEdits(prev => ({
                                  ...prev,
                                  [selectedEmailGroup]: {
                                    to: group.emailPreview.to || '',
                                    cc: group.emailPreview.cc || '',
                                    subject: group.emailPreview.subject || '',
                                    html: group.emailPreview.html || ''
                                  }
                                }));
                              }
                            }}
                          >
                            Ripristina originale
                          </Button>
                        </Alert>
                      )}
                    </Box>
                  ) : (
                    <Alert severity="warning">
                      Anteprima non disponibile per questo fornitore (email mancante)
                    </Alert>
                  )}
                </Box>
              )}
            </>
          ) : (
            <Alert severity="warning">
              Nessun ordine valido da inviare. Verifica che gli ordini selezionati abbiano un fornitore con email.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEmailPreviewOpen(false)} 
            disabled={emailSending}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={emailSending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            onClick={handleSendOrdersEmail}
            disabled={emailSending || !emailPreviewData?.groups?.some(g => g.canSend)}
          >
            {emailSending ? 'Invio in corso...' : 'Invia Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RefillsPage;
